import React from "react";
import { PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { usePiopiy } from "../context/PiopiyContext";

/**
 * CallCustomerButton — small "📞 Call" pill shown next to any customer
 * phone number. Uses the shared PIOPIY SDK context, so the same button
 * works identically everywhere (Customer 360, ChatsPage header, Leads
 * page row, etc).
 *
 * Behaviour:
 *   • Disabled with an explanatory tooltip when the agent's phone is
 *     offline or there's already an active call.
 *   • Hidden entirely when the current user has no SDK creds configured
 *     (so non-phone users / viewers aren't teased with a broken button).
 */

const CallCustomerButton = ({ phone, customerName, leadId, size = "sm", label = "Call" }) => {
  const { creds, call, makeCall } = usePiopiy();

  // Hide when this agent doesn't have CHUB creds — cleaner than showing
  // a permanently-disabled button that confuses them.
  if (!creds?.configured) return null;
  if (!phone) return null;

  // Only disabled while a click-to-call trigger is pending on-screen
  // (prevents double-firing). With CHUB there's no "online/offline" SDK
  // state — the softphone handles audio.
  const disabled = !!call;

  const onClick = () => {
    if (call) {
      toast.error("Call trigger already pending — answer your softphone");
      return;
    }
    makeCall(phone, { leadId, customerName });
  };

  const sizeClasses = size === "md"
    ? "px-3 py-1.5 text-xs"
    : "px-2 py-1 text-[10px]";

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={disabled
        ? "Softphone trigger pending — answer it"
        : `Call ${phone} via TeleCMI (rings your softphone first)`}
      className={`inline-flex items-center gap-1 ${sizeClasses} rounded-full font-bold border transition-colors ${
        disabled
          ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
          : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
      }`}
      data-testid="call-customer-btn"
    >
      <PhoneCall size={size === "md" ? 14 : 12} />
      {label}
    </button>
  );
};

export default CallCustomerButton;
